import React, { useState, useEffect } from 'react';
import { MAIN_NAV } from '../constants';
import { ChevronDown, Menu as MenuIcon, X, Search, Home, Facebook, Twitter, Youtube, Instagram } from 'lucide-react';
import { cn } from '../lib/utils';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslate } from '../context/LanguageContext';
import { TikTok } from './TikTokIcon';
import { useData } from '../context/DataContext';
import { API_BASE_URL } from '../api';

export default function Navbar() {
  const { language, toggleLanguage, t, translateNav } = useTranslate();
  const { settings } = useData();
  const socialLinks = settings?.social_links || {};
  const navItems = translateNav(MAIN_NAV);
  const logoUrl = settings?.logo_url ? (settings.logo_url.startsWith('http') ? settings.logo_url : `${API_BASE_URL.replace('/api', '')}${settings.logo_url}`) : null;
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    if (typeof window !== 'undefined') {
      const persisted = localStorage.getItem('theme');
      if (persisted === 'light' || persisted === 'dark') {
        return persisted;
      }
      return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
    }
    return 'light';
  });

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
    // Dispatch a global custom event for immediate synchronization across all components on the current page
    window.dispatchEvent(new CustomEvent('theme-change', { detail: theme }));
  }, [theme]);

  useEffect(() => {
    const handleThemeChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      const newTheme = customEvent.detail;
      if (newTheme === 'light' || newTheme === 'dark') {
        setTheme((prev) => (prev !== newTheme ? newTheme : prev));
      }
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'theme' && (e.newValue === 'light' || e.newValue === 'dark')) {
        setTheme((prev) => (prev !== e.newValue ? (e.newValue as 'light' | 'dark') : prev));
      }
    };

    window.addEventListener('theme-change', handleThemeChange);
    window.addEventListener('storage', handleStorageChange);
    
    // Quick immediate check to make sure navbar is precisely synced with root class on mount
    const currentIsDark = document.documentElement.classList.contains('dark');
    const expectedTheme = currentIsDark ? 'dark' : 'light';
    setTheme((prev) => (prev !== expectedTheme ? expectedTheme : prev));

    return () => {
      window.removeEventListener('theme-change', handleThemeChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  return (
    <nav className={cn(
      "backdrop-blur-md border-b sticky top-0 z-[100] transition-colors duration-300 w-full",
      isScrolled 
        ? "bg-secondary border-secondary shadow-lg" 
        : "bg-white/80 dark:bg-zinc-950/80 border-zinc-100 dark:border-zinc-900"
    )}>
      <div className="w-full px-4 lg:px-8 xl:px-12">
        <div className="flex items-center justify-between h-20">
          
          {/* Right: Logo Brand */}
          <div className="flex-shrink-0 flex justify-start">
            <a href="/" className="group flex items-center gap-3">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Logo"
                  className="h-12 w-auto object-contain rounded-lg shadow-lg group-hover:shadow-secondary/20 transition-all transform group-hover:-translate-y-1"
                />
              ) : (
                <div className="relative w-12 h-12 bg-black flex items-center justify-center rounded-lg shadow-lg group-hover:shadow-secondary/20 transition-all transform group-hover:rotate-3 translate-y-0 group-hover:-translate-y-1">
                  <span className="text-white font-black text-2xl italic tracking-tighter">NK</span>
                  <div className="absolute -bottom-1.5 -left-1.5 w-5 h-5 bg-secondary rounded shadow-md group-hover:-rotate-12 transition-transform" />
                </div>
              )}
            </a>
          </div>

          {/* Center: Desktop Navigation Links */}
          <div className="hidden lg:flex items-center justify-center gap-4 xl:gap-6 flex-1">
            {navItems.map((item) => (
              <div 
                key={item.label}
                className="relative group py-6"
                onMouseEnter={() => setActiveDropdown(item.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <a 
                  href={item.href}
                  className={cn(
                    "px-4 py-2 text-[12.5px] font-normal transition-all duration-300 flex items-center gap-2 relative rounded-full whitespace-nowrap",
                    activeDropdown === item.label 
                      ? (isScrolled ? "bg-white text-secondary shadow-lg" : "text-white bg-secondary shadow-lg shadow-secondary/20")
                      : (isScrolled ? "text-white/90 hover:bg-white hover:text-secondary" : "text-zinc-600 dark:text-zinc-400 hover:text-white hover:bg-secondary hover:shadow-lg hover:shadow-secondary/20")
                  )}
                >
                  {['الرئيسية', 'Home'].includes(item.label) && <Home size={14} className={cn("opacity-100 transition-colors duration-300", activeDropdown === item.label ? (isScrolled ? "text-secondary" : "text-white") : (isScrolled ? "text-white" : "text-zinc-600 dark:text-zinc-400"))} />}
                  {item.label}
                  {item.children && <ChevronDown size={14} className={cn("transition-transform duration-300", activeDropdown === item.label ? "rotate-180 opacity-100" : "opacity-40 group-hover:rotate-180", isScrolled && activeDropdown !== item.label ? "text-white" : "")} />}
                </a>

                {/* Dropdown Menu */}
                <AnimatePresence>
                  {item.children && activeDropdown === item.label && (
                    <motion.div
                      initial={{ opacity: 0, y: 12, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.96 }}
                      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute top-[85%] right-0 w-64 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-xl border border-zinc-100 dark:border-zinc-800 shadow-2xl rounded-2xl overflow-hidden p-2.5 pt-4 z-50 origin-top-right"
                    >
                      <div className="space-y-1">
                        {item.children.map((child, idx) => (
                          <motion.a
                            initial={{ opacity: 0, x: 8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.03, duration: 0.25, ease: "easeOut" }}
                            key={child.label}
                            href={child.href}
                            className="group/child flex items-center gap-3 px-4 py-3 text-sm font-normal text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800/60 hover:text-secondary rounded-xl transition-all duration-300"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-zinc-200 dark:bg-zinc-700 group-hover/child:bg-secondary group-hover/child:scale-125 transition-all duration-300" />
                            {child.label}
                          </motion.a>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          {/* Left: Tools & Mobile Toggle */}
          <div className="flex items-center gap-3 justify-end">
            
            {/* Elegant Language Toggle in Gold & Dark Palette */}
            <button
              onClick={toggleLanguage}
              className={cn(
                "h-10 px-4 flex items-center gap-2 rounded-full border transition-all duration-300 shadow-lg select-none active:scale-95 cursor-pointer relative overflow-hidden group font-bold text-[11px] tracking-wider",
                isScrolled ? "bg-white text-secondary border-white hover:shadow-white/20" : "bg-zinc-950 text-secondary border-secondary/20 hover:border-secondary dark:border-secondary/10 dark:hover:border-secondary hover:text-white hover:shadow-secondary/10"
              )}
              title={language === 'ar' ? 'Change Language to English' : 'تغيير لغة الموقع إلى العربية'}
            >
              <div className={cn("absolute inset-0 bg-gradient-to-r from-transparent to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000", isScrolled ? "via-secondary/10" : "via-secondary/15")} />
              <span className="text-secondary select-none">🌐</span>
              <span>{language === 'ar' ? 'English' : 'العربية'}</span>
            </button>

            {/* Desktop Expanding Search */}
            <div className={cn(
              "hidden lg:flex items-center relative transition-all duration-500 ease-in-out",
              isSearchExpanded ? "w-64" : "w-10"
            )}>
              <AnimatePresence mode="wait">
                {isSearchExpanded && (
                  <motion.div
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: '100%', opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    className="w-full relative"
                  >
                    <input
                      autoFocus
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onBlur={() => !searchQuery && setIsSearchExpanded(false)}
                      placeholder={t("بحث عن المواضيع، الكتاب...")}
                      className="w-full h-10 bg-zinc-100 dark:bg-zinc-900 rounded-full pr-10 pl-10 text-[11px] font-black text-zinc-700 dark:text-zinc-200 focus:outline-none focus:ring-2 focus:ring-secondary/20 transition-all border border-transparent focus:border-secondary/30"
                    />
                    {searchQuery && (
                      <button 
                        onClick={() => setSearchQuery('')}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-secondary transition-colors"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
              <button 
                onClick={() => setIsSearchExpanded(!isSearchExpanded)}
                className={cn(
                  "p-2.5 rounded-full transition-all flex items-center justify-center shrink-0",
                  isScrolled ? "hover:bg-white/20 text-white" : "hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-500 dark:text-zinc-400",
                  isSearchExpanded ? "absolute right-0 text-secondary" : (isScrolled ? "relative" : "relative border border-zinc-100 dark:border-zinc-800 shadow-sm")
                )}
                aria-label="Search"
              >
                <Search size={18} />
              </button>
            </div>

            {/* Mobile Search (Click opens mobile menu) */}
            <button 
              onClick={() => setIsOpen(true)}
              className={cn(
                "lg:hidden p-2.5 rounded-xl transition-all border shadow-sm",
                isScrolled ? "hover:bg-white/20 text-white border-transparent" : "hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-500 dark:text-zinc-400 border-zinc-100 dark:border-zinc-800"
              )}
            >
              <Search size={18} />
            </button>
            <button 
              onClick={() => setIsOpen(!isOpen)}
              className={cn(
                "lg:hidden flex items-center justify-center w-10 h-10 rounded-xl border shadow-sm hover:shadow-md transition-all group overflow-hidden relative z-[210]",
                isScrolled ? "bg-white/20 border-white/20 text-white" : "bg-white dark:bg-zinc-900 border-zinc-100 dark:border-zinc-800"
              )}
              aria-label="Toggle Menu"
            >
              <div className={cn("absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300", isScrolled ? "bg-white/10" : "bg-secondary/5")} />
              <AnimatePresence mode="wait">
                {isOpen ? (
                  <motion.div
                    key="close"
                    initial={{ scale: 0, rotate: -90 }}
                    animate={{ scale: 1, rotate: 0 }}
                    exit={{ scale: 0, rotate: 90 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X size={20} className={cn(isScrolled ? "text-white" : "text-secondary")} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ scale: 0, rotate: 90 }}
                    animate={{ scale: 1, rotate: 0 }}
                    exit={{ scale: 0, rotate: -90 }}
                    transition={{ duration: 0.2 }}
                  >
                    <MenuIcon size={20} className={cn("transition-colors", isScrolled ? "text-white" : "text-zinc-600 dark:text-zinc-300 group-hover:text-secondary")} />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>

        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] overflow-hidden"
          >
            {/* Glossy Backdrop */}
            <div 
              className="absolute inset-0 bg-white/95 dark:bg-zinc-950/98 backdrop-blur-2xl cursor-pointer" 
              onClick={() => setIsOpen(false)}
            />
            
            {/* Animated Mesh Decoration */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20 dark:opacity-40">
              <div className="absolute -top-[20%] -right-[10%] w-[80%] h-[80%] bg-secondary/20 blur-[120px] rounded-full animate-pulse" />
              <div className="absolute -bottom-[20%] -left-[10%] w-[60%] h-[60%] bg-primary/20 blur-[100px] rounded-full" />
            </div>

            <div 
              className={cn("relative h-full flex flex-col p-6 pt-24 overflow-y-auto cursor-default scrollbar-hide", language === 'ar' ? "text-right" : "text-left")}
              onClick={() => setIsOpen(false)}
            >
              {/* Header inside mobile menu */}
              <div 
                className="absolute top-4 right-4 left-4 flex justify-between items-center z-50"
                onClick={(e) => e.stopPropagation()}
              >
                  <div className="flex items-center gap-3">
                    <div className="relative w-10 h-10 bg-black flex items-center justify-center rounded-lg shadow-lg">
                      <span className="text-white font-black text-lg italic tracking-tighter">NK</span>
                      <div className="absolute -bottom-1 -left-1 w-4 h-4 bg-secondary rounded shadow-sm" />
                    </div>
                    <div className={cn("flex flex-col", language === 'ar' ? "items-start" : "items-start")}>
                      <span className="text-lg font-extrabold text-primary dark:text-white leading-none tracking-tighter italic">{t('نورالدين')}</span>
                      <span className="text-sm font-bold text-secondary -mt-0.5 leading-none italic">{t('كوكى')}</span>
                    </div>
                  </div>
                  <button 
                    onClick={() => setIsOpen(false)}
                    className="w-10 h-10 flex items-center justify-center bg-zinc-100 dark:bg-zinc-800/50 rounded-xl text-zinc-900 dark:text-white hover:bg-secondary hover:text-white transition-all active:scale-95"
                  >
                    <X size={24} />
                  </button>
              </div>

              <div 
                className="max-w-md mx-auto w-full space-y-8 pb-10"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Search in Menu */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="relative group"
                >
                  <input 
                     type="text" 
                     placeholder={t("ابحث عن موضوع...")}
                     className={cn(
                       "w-full bg-zinc-100 dark:bg-zinc-900/50 border border-transparent focus:border-secondary/30 p-4 rounded-xl text-base focus:outline-none transition-all font-medium placeholder:text-zinc-400 dark:text-white",
                       language === 'ar' ? "text-right pr-5 pl-14" : "text-left pl-5 pr-14"
                     )}
                     value={searchQuery}
                     onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <div className={cn("absolute top-1/2 -translate-y-1/2 p-2 bg-secondary rounded-lg text-zinc-950 shadow-md shadow-secondary/20", language === 'ar' ? "left-3" : "right-3")}>
                    <Search size={16} />
                  </div>
                </motion.div>

                {/* Primary Nav Items */}
                <nav className="flex flex-col gap-4">
                  {navItems.map((item, idx) => (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, x: language === 'ar' ? 30 : -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.15 + (idx * 0.05) }}
                    >
                      <div className="flex items-center justify-between group">
                        <a 
                          href={item.href}
                          className={cn(
                            "text-2xl sm:text-3xl md:text-4xl font-black transition-all",
                            activeDropdown === item.label ? "text-secondary" : "text-zinc-900 dark:text-white/90 hover:text-secondary",
                            language === 'ar' ? "hover:translate-x-[-8px]" : "hover:translate-x-[8px]"
                          )}
                          onClick={() => !item.children && setIsOpen(false)}
                        >
                          {item.label}
                        </a>
                        {item.children && (
                          <button 
                            onClick={() => setActiveDropdown(activeDropdown === item.label ? null : item.label)}
                            className={cn(
                              "p-3 rounded-xl transition-all active:scale-95",
                              activeDropdown === item.label ? "bg-secondary text-zinc-950" : "bg-zinc-100 dark:bg-zinc-800/60 text-zinc-500 dark:text-zinc-400"
                            )}
                          >
                            <ChevronDown size={20} className={cn("transition-transform duration-500", activeDropdown === item.label && "rotate-180")} />
                          </button>
                        )}
                      </div>
                      
                      <AnimatePresence>
                        {item.children && activeDropdown === item.label && (
                          <motion.div 
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className={cn(
                              "mt-4 space-y-4 overflow-hidden",
                              language === 'ar' ? "mr-2 border-r-2 border-secondary/30 pr-5" : "ml-2 border-l-2 border-secondary/30 pl-5"
                            )}
                          >
                            {item.children.map((child) => (
                              <a 
                                key={child.label}
                                href={child.href}
                                className="block text-lg sm:text-xl font-bold text-zinc-500 dark:text-zinc-400 hover:text-secondary transition-all"
                                onClick={() => setIsOpen(false)}
                              >
                                {child.label}
                              </a>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </nav>

                {/* Footer in Menu */}
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="pt-8 space-y-8 border-t border-zinc-100 dark:border-zinc-800/60 mt-4"
                >
                   <div className="flex justify-center gap-3">
                      {[
                        { icon: <Facebook size={18} />, label: 'facebook', href: socialLinks.facebook || "#" },
                        { icon: <Twitter size={18} />, label: 'twitter', href: socialLinks.twitter || "#" },
                        { icon: <Youtube size={18} />, label: 'youtube', href: socialLinks.youtube || "#" },
                        { icon: <Instagram size={18} />, label: 'instagram', href: socialLinks.instagram || "#" },
                        { icon: <TikTok size={18} />, label: 'tiktok', href: socialLinks.tiktok || "#" }
                      ].map((social) => (
                        <a 
                          key={social.label} 
                          href={social.href}
                          target={social.href !== "#" ? "_blank" : undefined}
                          rel={social.href !== "#" ? "noopener noreferrer" : undefined}
                          className="w-10 h-10 flex items-center justify-center bg-zinc-100 dark:bg-zinc-800/60 rounded-xl text-zinc-500 dark:text-zinc-400 hover:bg-secondary hover:text-zinc-950 transition-all transform hover:-translate-y-1"
                        >
                          {social.icon}
                        </a>
                      ))}
                   </div>
                   <div className="text-center">
                     <p className="text-zinc-400 dark:text-zinc-500 text-[10px] font-bold leading-relaxed max-w-xs mx-auto">
                        {t("صحافة المواطن المستقلة - رؤية وطنية متجددة")} <br />
                        <span className="text-secondary">#{language === 'ar' ? 'الميدان_الثوري' : 'RevolutionaryField'}</span>
                     </p>
                   </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
